// File: frontend/src/components/marketing/MarketingFooter.js
//
// Public footer: official brand lockup + "Access. Pay. Grow.", product links,
// account actions, contact and support, legal links, dynamic copyright year.
//
// Decisions:
// - Contact details come from config/publicSupport.js (the one public support
//   line). There is no general support mailbox, so no email is shown here. The
//   legal and privacy mailboxes stay inside the legal documents.
// - "Support" goes to /contact, a real destination. It used to point at the
//   FAQ, whose support answer pointed back at a footer that had no contact
//   details: a loop with no way out.
// - Product links are written "/#section" so they work from every page that
//   renders this footer, including /privacy, /terms and /ambassador.
// - Links are at least 44px tall on phones and 32px from md up.
// - The bottom padding clears the floating WhatsApp button (56px + 16/24px
//   offset), so it never covers the last row when scrolled to the end.
// - NO password-recovery link appears here.

import React from "react";
import { Box, Container, Stack, Typography, Divider } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import PesaFiiBrand from "../PesaFiiBrand";
import {
  SUPPORT_PHONE_DISPLAY, SUPPORT_TEL_HREF, WHATSAPP_MESSAGES, whatsappUrl,
} from "../../config/publicSupport";

const LINK_SX = {
  display: "flex",
  alignItems: "center",
  minHeight: { xs: 44, md: 32 },
  width: "fit-content",
  color: "text.secondary",
  textDecoration: "none",
  fontSize: 14,
  fontWeight: 500,
  "&:hover": { color: "text.primary" },
  "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
};

function FooterLink({ link }) {
  if (link.to) {
    return <Box component={RouterLink} to={link.to} sx={LINK_SX}>{link.label}</Box>;
  }
  const external = /^https?:/.test(link.href);
  return (
    <Box
      component="a"
      href={link.href}
      aria-label={link.ariaLabel}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      sx={LINK_SX}
    >
      {link.label}
    </Box>
  );
}

function FooterColumn({ heading, links }) {
  const id = `footer-${heading.toLowerCase().replace(/[^a-z]+/g, "-")}`;
  return (
    <Box component="nav" aria-labelledby={id} sx={{ minWidth: 0 }}>
      <Typography
        id={id}
        component="p"
        sx={{ fontSize: 12.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.disabled", mb: { xs: 0.5, md: 1 } }}
      >
        {heading}
      </Typography>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {links.map((l) => (
          <li key={l.label}><FooterLink link={l} /></li>
        ))}
      </Box>
    </Box>
  );
}

const PRODUCT_LINKS = [
  { label: "Product", href: "/#product" },
  { label: "Features", href: "/#features" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Solutions", href: "/#solutions" },
  { label: "Pricing", href: "/#pricing" },
];

export default function MarketingFooter({ isAuthed, whatsappMessage = WHATSAPP_MESSAGES.general }) {
  const year = new Date().getFullYear();

  const accountLinks = [
    ...(isAuthed
      ? [{ label: "Open Dashboard", to: "/dashboard" }]
      : [
          { label: "Sign In", to: "/login?mode=login" },
          { label: "Start Free", to: "/login?mode=register" },
        ]),
    { label: "PesaFii Ambassadors", to: "/ambassador" },
  ];

  const supportLinks = [
    { label: "Contact PesaFii", to: "/contact" },
    { label: "WhatsApp", href: whatsappUrl(whatsappMessage), ariaLabel: "Chat with PesaFii on WhatsApp (opens WhatsApp)" },
    { label: `Call ${SUPPORT_PHONE_DISPLAY}`, href: SUPPORT_TEL_HREF },
    { label: "FAQ", href: "/#faq" },
  ];

  return (
    <Box
      component="footer"
      sx={{
        borderTop: "1px solid", borderColor: "divider", bgcolor: "action.hover",
        mt: { xs: 2, md: 4 },
        // /ambassador paints a fixed decorative backdrop at z-index 0. Without a
        // stacking position of its own the footer sits underneath it and is
        // covered. Harmless on every other public page.
        position: "relative", zIndex: 1,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 7 }, pb: 12, minWidth: 0 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1.6fr 1fr 1fr 1.1fr" }, columnGap: { xs: 3, md: 6 }, rowGap: 4 }}>
          {/* Brand */}
          <Box sx={{ minWidth: 0, gridColumn: { xs: "1 / -1", md: "auto" } }}>
            <PesaFiiBrand linkTo="/" ariaLabel="PesaFii home" />
            <Typography sx={{ mt: 1.5, fontSize: 14, fontWeight: 700, color: "primary.main", letterSpacing: "0.02em" }}>
              Access. Pay. Grow.
            </Typography>
            <Typography sx={{ mt: 1, fontSize: 13.5, color: "text.secondary", maxWidth: 320, lineHeight: 1.6 }}>
              Hotspot billing and network management for operators, network businesses and growing internet providers.
            </Typography>
          </Box>

          <FooterColumn heading="Product" links={PRODUCT_LINKS} />
          <FooterColumn heading="Get started" links={accountLinks} />
          <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
            <FooterColumn heading="Contact & support" links={supportLinks} />
          </Box>
        </Box>

        <Divider sx={{ my: { xs: 3, md: 4 }, borderColor: "divider" }} />

        <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 0.5, md: 2 }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            © {year} PesaFii · Operated by Agrinal Enterprises Limited, Uganda
          </Typography>
          <Stack direction="row" spacing={2.5} alignItems="center" useFlexGap flexWrap="wrap">
            <FooterLink link={{ label: "Privacy", to: "/privacy" }} />
            <FooterLink link={{ label: "Terms", to: "/terms" }} />
            <Typography sx={{ fontSize: 12.5, color: "text.disabled" }}>
              Built for hotspot operators in Uganda.
            </Typography>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
